import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ConditionBuilder = ({ triggerType, conditions, onChange }) => {
  const [draggedCondition, setDraggedCondition] = useState(null);

  const operatorOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'greater_than_or_equal', label: 'Greater Than or Equal' },
    { value: 'less_than_or_equal', label: 'Less Than or Equal' },
    { value: 'between', label: 'Between' },
    { value: 'in', label: 'In' },
    { value: 'not_in', label: 'Not In' },
    { value: 'contains', label: 'Contains' }
  ];

  const logicalOperatorOptions = [
    { value: 'AND', label: 'AND' },
    { value: 'OR', label: 'OR' }
  ];

  const getFieldOptionsForTrigger = (trigger) => {
    const fieldMap = {
      client_revenue_threshold: [
        { value: 'total_revenue', label: 'Total Revenue', type: 'number' },
        { value: 'service_count', label: 'Service Count', type: 'number' },
        { value: 'client_type', label: 'Client Type', type: 'text' }
      ],
      service_frequency: [
        { value: 'service_interval_days', label: 'Service Interval (Days)', type: 'number' },
        { value: 'service_count', label: 'Service Count', type: 'number' },
        { value: 'last_service_date', label: 'Last Service Date', type: 'date' }
      ],
      visit_count_milestone: [
        { value: 'visit_count', label: 'Visit Count', type: 'number' },
        { value: 'first_visit_date', label: 'First Visit Date', type: 'date' }
      ],
      membership_anniversary: [
        { value: 'membership_years', label: 'Membership Years', type: 'number' },
        { value: 'first_service_date', label: 'First Service Date', type: 'date' }
      ],
      invoice_amount: [
        { value: 'invoice_amount', label: 'Invoice Amount', type: 'number' },
        { value: 'invoice_status', label: 'Invoice Status', type: 'text' }
      ],
      payment_status: [
        { value: 'payment_status', label: 'Payment Status', type: 'text' },
        { value: 'days_overdue', label: 'Days Overdue', type: 'number' }
      ],
      client_lifetime_value: [
        { value: 'client_lifetime_value', label: 'Client Lifetime Value', type: 'number' },
        { value: 'service_count', label: 'Service Count', type: 'number' }
      ],
      service_interval: [
        { value: 'days_since_last_service', label: 'Days Since Last Service', type: 'number' },
        { value: 'average_service_interval', label: 'Average Service Interval', type: 'number' }
      ],
      feedback_rating_threshold: [
        { value: 'satisfaction_rating', label: 'Satisfaction Rating (1-5)', type: 'number' },
        { value: 'follow_up_requested', label: 'Follow-up Requested', type: 'boolean' },
        { value: 'submitted_at', label: 'Feedback Submitted Date', type: 'date' }
      ],
      service_issue_detection: [
        { value: 'service_comments', label: 'Service Comments (Keywords)', type: 'text' },
        { value: 'satisfaction_rating', label: 'Satisfaction Rating', type: 'number' },
        { value: 'follow_up_requested', label: 'Follow-up Requested', type: 'boolean' }
      ]
    };

    return fieldMap?.[trigger] || [];
  };

  const fieldOptions = getFieldOptionsForTrigger(triggerType);

  const addCondition = () => {
    const newCondition = {
      id: `cond-${Date.now()}`,
      conditionGroup: 1,
      fieldName: fieldOptions?.[0]?.value || '',
      operator: 'equals',
      valueText: '',
      valueNumber: null,
      valueDate: null,
      valueBoolean: null,
      valueArray: null,
      logicalOperator: 'AND',
      sortOrder: conditions?.length
    };
    onChange([...conditions, newCondition]);
  };

  const removeCondition = (conditionId) => {
    onChange(conditions?.filter(c => c?.id !== conditionId));
  };

  const updateCondition = (conditionId, updates) => {
    onChange(
      conditions?.map(c => 
        c?.id === conditionId ? { ...c, ...updates } : c
      )
    );
  };

  const getValueInputType = (fieldName) => {
    const field = fieldOptions?.find(f => f?.value === fieldName);
    return field?.type || 'text';
  };

  // Drag and drop handlers
  const handleDragStart = (e, condition) => {
    setDraggedCondition(condition);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetCondition) => {
    e?.preventDefault();
    if (!draggedCondition || draggedCondition?.id === targetCondition?.id) return;

    const draggedIndex = conditions?.findIndex(c => c?.id === draggedCondition?.id);
    const targetIndex = conditions?.findIndex(c => c?.id === targetCondition?.id);

    const newConditions = [...conditions];
    newConditions?.splice(draggedIndex, 1);
    newConditions?.splice(targetIndex, 0, draggedCondition);

    // Update sort order
    const reorderedConditions = newConditions?.map((c, index) => ({
      ...c,
      sortOrder: index
    }));

    onChange(reorderedConditions);
    setDraggedCondition(null);
  };

  const handleDragEnd = () => {
    setDraggedCondition(null);
  };

  const renderValueInput = (condition) => {
    const inputType = getValueInputType(condition?.fieldName);

    if (inputType === 'boolean') {
      return (
        <Select
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' }
          ]}
          value={condition?.valueBoolean?.toString() || 'false'}
          onChange={(value) => updateCondition(condition?.id, { 
            valueBoolean: value === 'true' 
          })}
        />
      );
    }

    if (inputType === 'number') {
      return (
        <Input
          type="number"
          placeholder="Enter value"
          value={condition?.valueNumber || ''}
          onChange={(e) => updateCondition(condition?.id, { 
            valueNumber: parseFloat(e?.target?.value) || null 
          })}
        />
      );
    }

    if (inputType === 'date') {
      return (
        <Input
          type="date"
          value={condition?.valueDate || ''}
          onChange={(e) => updateCondition(condition?.id, { 
            valueDate: e?.target?.value 
          })}
        />
      );
    }

    // Text input with keyword suggestions for service_comments
    if (condition?.fieldName === 'service_comments') {
      return (
        <div>
          <Input
            type="text"
            placeholder="Enter keywords (e.g., problema, falla, queja)"
            value={condition?.valueText || ''}
            onChange={(e) => updateCondition(condition?.id, { 
              valueText: e?.target?.value 
            })}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {['problema', 'falla', 'queja', 'insatisfecho', 'deficiente', 'mal servicio', 'retraso']?.map(keyword => (
              <button
                key={keyword}
                type="button"
                onClick={() => updateCondition(condition?.id, { 
                  valueText: condition?.valueText ? `${condition?.valueText}, ${keyword}` : keyword
                })}
                className="px-2 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20 transition-smooth"
              >
                + {keyword}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <Input
        type="text"
        placeholder="Enter value"
        value={condition?.valueText || ''}
        onChange={(e) => updateCondition(condition?.id, { 
          valueText: e?.target?.value 
        })}
      />
    );
  };

  // Render feedback-specific guidance
  const renderFeedbackGuidance = () => {
    if (triggerType === 'feedback_rating_threshold') {
      return (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div className="text-sm text-foreground">
              <strong>Feedback Rating Threshold:</strong> Trigger emails when customer satisfaction ratings meet specific conditions. 
              Example: Send follow-up email when rating ≤ 3 stars.
            </div>
          </div>
        </div>
      );
    }

    if (triggerType === 'service_issue_detection') {
      return (
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
            <div className="text-sm text-foreground">
              <strong>Service Issue Detection:</strong> Automatically detect issues in customer feedback comments using keyword matching. 
              Common keywords: problema, falla, queja, insatisfecho, deficiente.
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Condition Logic</h3>
          <p className="text-sm text-muted-foreground">
            Define when this automation rule should trigger {conditions?.length > 0 && '(drag to reorder)'}
          </p>
        </div>
        <button
          type="button"
          onClick={addCondition}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
        >
          <Icon name="Plus" size={16} />
          <span>Add Condition</span>
        </button>
      </div>

      {renderFeedbackGuidance()}

      {conditions?.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-lg border-2 border-dashed border-border">
          <Icon name="Filter" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">
            No conditions added yet. Click "Add Condition" to start building your rule logic.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {conditions?.map((condition, index) => (
            <div 
              key={condition?.id} 
              className={`bg-muted/30 border border-border rounded-lg p-4 transition-smooth ${
                draggedCondition?.id === condition?.id ? 'opacity-50' : ''
              } hover:shadow-md cursor-move`}
              draggable
              onDragStart={(e) => handleDragStart(e, condition)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, condition)}
              onDragEnd={handleDragEnd}
            >
              <div className="grid grid-cols-12 gap-3 items-start">
                {/* Drag Handle */}
                <div className="col-span-12 md:col-span-1 flex items-center justify-center">
                  <Icon name="GripVertical" size={20} className="text-muted-foreground" />
                </div>

                {/* Logical Operator (for conditions after first) */}
                {index > 0 && (
                  <div className="col-span-12 mb-2">
                    <Select
                      options={logicalOperatorOptions}
                      value={condition?.logicalOperator}
                      onChange={(value) => updateCondition(condition?.id, { logicalOperator: value })}
                    />
                  </div>
                )}

                {/* Field Name */}
                <div className="col-span-12 md:col-span-4">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Field
                  </label>
                  <Select
                    options={fieldOptions}
                    value={condition?.fieldName}
                    onChange={(value) => updateCondition(condition?.id, { fieldName: value })}
                  />
                </div>

                {/* Operator */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Operator
                  </label>
                  <Select
                    options={operatorOptions}
                    value={condition?.operator}
                    onChange={(value) => updateCondition(condition?.id, { operator: value })}
                  />
                </div>

                {/* Value */}
                <div className="col-span-12 md:col-span-3">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Value
                  </label>
                  {renderValueInput(condition)}
                </div>

                {/* Remove Button */}
                <div className="col-span-12 md:col-span-1 flex items-end">
                  <button
                    type="button"
                    onClick={() => removeCondition(condition?.id)}
                    className="w-full md:w-auto p-2 bg-error/10 text-error rounded-lg hover:bg-error/20 transition-smooth"
                    title="Remove condition"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {conditions?.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div className="text-sm text-foreground">
              <strong>Condition Logic:</strong> All conditions will be evaluated together. 
              Use AND to require all conditions, or OR to trigger when any condition is met.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConditionBuilder;